import { isConversationLanguage, type ConversationLanguage } from "@digital-twins/shared-types";

import { chatT } from "@/i18n";

/**
 * The language the builder's chat speaks (wave G, D): the draft's one
 * `conversationLanguage`, as the session last read it from the transcript.
 *
 * A chat message words its own code lines from the language it carries
 * (`message.language`). The lines no message carries (a turn's failure, a
 * Save's notice, a toast) are worded in this one. `undefined` means a draft
 * stored before the language was kept, whose lines are the interface's.
 *
 * Every thread line the chat component composes reads it here, as
 * `chatT(chatLanguage())`, or from its message (`message.language`), never from
 * the component's interface `t`.
 *
 * shortcut: module state, because those lines are raised from hooks and
 * utilities outside React (the workspace hook's Save notices and write
 * failures, the session hook's failure bubbles, `questionnaireIssueCopy`'s
 * toasts), which share no parent that could pass the language down, and only
 * one builder chat is open at a time; deriving it from the session's transcript
 * state would only move the same snapshot (set when a transcript is adopted)
 * into a prop, and reach none of them. Revisit when a second chat can be open.
 */
let spoken: ConversationLanguage | undefined;

const primary = (code: string) => code.toLowerCase().split("-")[0];

/**
 * What the builder says, once, when the chat speaks another language than the
 * study (decided 2026-10-23): the objective, learning goals, assumptions and
 * report groups are report text, written in the study's language, and quoted
 * as written inside the chat. Both languages are the draft's typed facts
 * (`conversationLanguage`, `contentLanguage`); null when they are the same, or
 * either is not known. The study's language is named in the chat's own.
 */
export function studyLanguageNote(
  chat: string | undefined,
  study: string | null | undefined,
): string | null {
  if (!chat || !study || primary(chat) === primary(study)) return null;
  const language = new Intl.DisplayNames([chat], { type: "language" }).of(study) ?? study;
  return chatT(chat)("questionnaire.studyLanguageNote", { language });
}

export function setChatLanguage(language: unknown): void {
  spoken = isConversationLanguage(language) ? language : undefined;
}

export function chatLanguage(): ConversationLanguage | undefined {
  return spoken;
}
